import React, { useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import {
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
} from 'recharts';
import {
  BarChart3, Receipt, Users, XCircle, CreditCard, Wallet, Scale,
  Download, Loader2, RefreshCw, Save, Calendar, Filter, FileSpreadsheet,
  FileText, FileDown, Trash2, Search as SearchIcon, ChevronLeft, ChevronRight,
  CheckCircle2, AlertTriangle, Percent, Building2,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/reports`;

const REPORT_ICONS = {
  sales: BarChart3,
  bookings: Receipt,
  agent_performance: Users,
  cancellation: XCircle,
  payment_mode: CreditCard,
  credit_ledger: Scale,
  wallet_reconciliation: Wallet,
  gst_report: Percent,
};

const DATE_PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: '7d', label: 'Last 7d' },
  { id: '30d', label: 'Last 30d' },
  { id: 'mtd', label: 'MTD' },
  { id: 'ytd', label: 'YTD' },
  { id: 'custom', label: 'Custom' },
];

const COLORS = ['#0d9488', '#F5A623', '#6366f1', '#ef4444', '#8b5cf6', '#06b6d4', '#84cc16', '#ec4899'];

const resolvePreset = (preset) => {
  const now = new Date();
  const toStr = (d) => d.toISOString().slice(0, 10);
  switch (preset) {
    case 'today': {
      return { from: toStr(now), to: toStr(now) };
    }
    case 'yesterday': {
      const d = new Date(now); d.setDate(d.getDate() - 1);
      return { from: toStr(d), to: toStr(d) };
    }
    case '7d': {
      const d = new Date(now); d.setDate(d.getDate() - 7);
      return { from: toStr(d), to: toStr(now) };
    }
    case '30d': {
      const d = new Date(now); d.setDate(d.getDate() - 30);
      return { from: toStr(d), to: toStr(now) };
    }
    case 'mtd': {
      return { from: toStr(new Date(now.getFullYear(), now.getMonth(), 1)), to: toStr(now) };
    }
    case 'ytd': {
      return { from: toStr(new Date(now.getFullYear(), 0, 1)), to: toStr(now) };
    }
    default:
      return null;
  }
};

const ReportsDashboard = ({ initialReport = 'sales' }) => {
  const [types, setTypes] = useState({});
  const [filterOptions, setFilterOptions] = useState({});
  const [activeReport, setActiveReport] = useState(initialReport);
  const [preset, setPreset] = useState('30d');
  const [customFrom, setCustomFrom] = useState('');
  const [customTo, setCustomTo] = useState('');
  const [groupBy, setGroupBy] = useState('');
  const [filters, setFilters] = useState({});
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [presets, setPresets] = useState([]);
  const [savingPreset, setSavingPreset] = useState(false);
  const [agents, setAgents] = useState([]);
  const [gstTab, setGstTab] = useState('all');
  const [hotelPerf, setHotelPerf] = useState(null);
  const [hotelPerfLoading, setHotelPerfLoading] = useState(false);
  const [backfillLimit, setBackfillLimit] = useState(100);
  const [backfillLiveApi, setBackfillLiveApi] = useState(false);
  const [backfillForce, setBackfillForce] = useState(false);
  const [backfillLoading, setBackfillLoading] = useState(false);
  const [backfillResult, setBackfillResult] = useState(null);

  const reportMeta = types[activeReport] || {};
  const groupByOptions = reportMeta.group_by_options || [];
  const needsAgentDropdown = activeReport === 'credit_ledger' || activeReport === 'wallet_reconciliation';
  const isGstReport = activeReport === 'gst_report';

  const effectiveDate = useMemo(() => {
    if (preset === 'custom') return { from: customFrom, to: customTo };
    return resolvePreset(preset) || {};
  }, [preset, customFrom, customTo]);

  useEffect(() => {
    let cancelled = false;
    const from = effectiveDate.from;
    const to = effectiveDate.to;
    if (!from || !to) return undefined;
    (async () => {
      setHotelPerfLoading(true);
      try {
        const r = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/admin/reports/hotels`, {
          withCredentials: true,
          params: { date_from: from, date_to: to },
        });
        if (!cancelled) setHotelPerf(r.data);
      } catch (_e) {
        if (!cancelled) setHotelPerf(null);
      } finally {
        if (!cancelled) setHotelPerfLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [effectiveDate.from, effectiveDate.to]);

  // ── Load types once ──
  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/types`, { withCredentials: true });
        setTypes(r.data.reports || {});
        setFilterOptions(r.data.filter_options || {});
      } catch (_e) { /* silent */ }
    })();
    loadPresets();
    loadAgents();
  }, []);

  const loadAgents = async () => {
    try {
      const r = await axios.get(`${API}/agents`, { withCredentials: true });
      setAgents(r.data.agents || []);
    } catch (_e) { /* silent */ }
  };

  // Reset group_by when report changes
  useEffect(() => {
    setGroupBy(reportMeta.default_group_by || '');
    setFilters({});
    setSearch('');
    setPage(1);
    setGstTab('all');
  }, [activeReport, reportMeta.default_group_by]);

  const loadPresets = async () => {
    try {
      const r = await axios.get(`${API}/presets`, { withCredentials: true });
      setPresets(r.data.presets || []);
    } catch (_e) { /* silent */ }
  };

  const build = useCallback(() => ({
    report_type: activeReport,
    date_from: effectiveDate.from || null,
    date_to: effectiveDate.to || null,
    group_by: groupBy || null,
    filters: { ...filters, ...(search ? { search } : {}) },
    sort_by: 'date_desc',
    page,
    limit: activeReport === 'bookings' ? 50 : 500,
  }), [activeReport, effectiveDate, groupBy, filters, search, page]);

  const run = useCallback(async () => {
    setLoading(true);
    try {
      const r = await axios.post(`${API}/generate`, build(), { withCredentials: true });
      setData(r.data);
    } catch (_e) {
      setData(null);
    } finally { setLoading(false); }
  }, [build]);

  // Auto-run on any config change
  useEffect(() => {
    if (!Object.keys(types).length) return;
    const t = setTimeout(run, 250);
    return () => clearTimeout(t);
  }, [activeReport, effectiveDate.from, effectiveDate.to, groupBy, filters, search, page, types, run]);

  const doExport = async (fmt) => {
    try {
      const r = await axios.post(`${API}/export`, { ...build(), export_format: fmt },
        { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([r.data]);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${activeReport}_${Date.now()}.${fmt}`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (_e) { /* silent */ }
  };

  const saveCurrentAsPreset = async () => {
    const name = window.prompt('Preset name:');
    if (!name) return;
    setSavingPreset(true);
    try {
      await axios.post(`${API}/presets`, {
        name,
        report_type: activeReport,
        filters,
        group_by: groupBy,
        date_from: effectiveDate.from,
        date_to: effectiveDate.to,
      }, { withCredentials: true });
      loadPresets();
    } finally { setSavingPreset(false); }
  };

  const applyPreset = (p) => {
    setActiveReport(p.report_type);
    setFilters(p.filters || {});
    setGroupBy(p.group_by || '');
    if (p.date_from && p.date_to) {
      setPreset('custom'); setCustomFrom(p.date_from.slice(0, 10)); setCustomTo(p.date_to.slice(0, 10));
    }
  };

  const deletePreset = async (id) => {
    if (!window.confirm('Delete this preset?')) return;
    try {
      await axios.delete(`${API}/presets/${id}`, { withCredentials: true });
      loadPresets();
    } catch (_e) { /* silent */ }
  };

  const runBackfill = async (dryRun) => {
    if (!dryRun) {
      const n = backfillResult?.summary?.would_update ?? backfillLimit;
      if (!window.confirm(`This will update fare_breakdown on up to ${n} bookings. Continue?`)) return;
    }
    setBackfillLoading(true);
    try {
      const r = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/maintenance/tripjack-economics-backfill`,
        {
          dry_run: dryRun,
          limit: backfillLimit,
          live_api: backfillLiveApi,
          cache_only: !backfillLiveApi,
          force: backfillForce,
        },
        { withCredentials: true },
      );
      setBackfillResult(r.data);
      if (!dryRun) run();
    } catch (e) {
      setBackfillResult({ success: false, error: e.response?.data?.detail || e.message });
    } finally {
      setBackfillLoading(false);
    }
  };

  return (
    <div className="p-4 md:p-6 max-w-[1600px] mx-auto" data-testid="admin-reports-page">
      <header className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Reports</h1>
          <p className="text-sm text-gray-600 mt-1">
            Dynamic reports across sales, bookings, agent performance and more. Export as CSV, Excel, or PDF.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={saveCurrentAsPreset}
            disabled={savingPreset}
            className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm hover:bg-gray-50"
            data-testid="reports-save-preset"
          >
            <Save className="w-4 h-4" /> Save preset
          </button>
          <div className="relative group">
            <button
              className="flex items-center gap-2 px-4 py-2 bg-[#F5A623] text-[#0C1D36] rounded-lg text-sm font-semibold hover:bg-[#F5A623]/90"
              data-testid="reports-export"
            >
              <Download className="w-4 h-4" /> Export
            </button>
            <div className="absolute right-0 top-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition z-10 min-w-[160px]">
              <button onClick={() => doExport('csv')} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2" data-testid="reports-export-csv">
                <FileDown className="w-4 h-4" /> CSV
              </button>
              <button onClick={() => doExport('xlsx')} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2" data-testid="reports-export-xlsx">
                <FileSpreadsheet className="w-4 h-4" /> Excel (.xlsx)
              </button>
              <button onClick={() => doExport('pdf')} className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2" data-testid="reports-export-pdf">
                <FileText className="w-4 h-4" /> PDF
              </button>
            </div>
          </div>
        </div>
      </header>

      <div className="mb-6 bg-white border border-gray-200 rounded-lg p-4" data-testid="tripjack-economics-backfill">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
          <div>
            <h2 className="text-sm font-bold text-gray-900">TripJack Economics Backfill</h2>
            <p className="text-xs text-gray-500 mt-0.5">Unify net fare, commission, and markup from TripJack cache or live API for legacy bookings.</p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-gray-600 flex items-center gap-1">
              Limit
              <input type="number" min={1} max={500} value={backfillLimit} onChange={(e) => setBackfillLimit(Number(e.target.value) || 100)}
                className="w-16 border border-gray-300 rounded px-2 py-1 text-sm" data-testid="backfill-limit" />
            </label>
            <label className="text-xs text-gray-600 flex items-center gap-1">
              <input type="checkbox" checked={backfillLiveApi} onChange={(e) => setBackfillLiveApi(e.target.checked)} data-testid="backfill-live-api" />
              Live TripJack API
            </label>
            <label className="text-xs text-gray-600 flex items-center gap-1">
              <input type="checkbox" checked={backfillForce} onChange={(e) => setBackfillForce(e.target.checked)} data-testid="backfill-force" />
              Force overwrite
            </label>
            <button type="button" disabled={backfillLoading} onClick={() => runBackfill(true)}
              className="px-3 py-2 rounded-lg text-sm font-semibold bg-amber-100 text-amber-900 border border-amber-300 hover:bg-amber-200 disabled:opacity-50"
              data-testid="backfill-dry-run">
              {backfillLoading ? <Loader2 className="w-4 h-4 animate-spin inline" /> : null} Preview (Dry Run)
            </button>
            <button type="button" disabled={backfillLoading} onClick={() => runBackfill(false)}
              className="px-3 py-2 rounded-lg text-sm font-semibold bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50"
              data-testid="backfill-apply-db">
              Apply to DB
            </button>
          </div>
        </div>
        {backfillResult?.summary && (
          <div className="text-xs text-gray-700 mb-2" data-testid="backfill-summary">
            Run <strong>{backfillResult.run_id}</strong> · scanned {backfillResult.summary.scanned} ·
            {backfillResult.dry_run ? ` would update ${backfillResult.summary.would_update}` : ` updated ${backfillResult.summary.updated}`} ·
            skipped {backfillResult.summary.skipped} · NF recovered {backfillResult.summary.nf_recovered}
            {backfillResult.summary.api_errors > 0 && ` · API errors ${backfillResult.summary.api_errors}`}
          </div>
        )}
        {backfillResult?.rows?.length > 0 && (
          <div className="overflow-x-auto max-h-48 border border-gray-100 rounded">
            <table className="min-w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1 text-left">Booking</th>
                  <th className="px-2 py-1 text-left">Tier</th>
                  <th className="px-2 py-1 text-left">Status</th>
                  <th className="px-2 py-1 text-right">NF before</th>
                  <th className="px-2 py-1 text-right">NF after</th>
                  <th className="px-2 py-1 text-right">Comm after</th>
                </tr>
              </thead>
              <tbody>
                {backfillResult.rows.slice(0, 20).map((row) => (
                  <tr key={row.booking_id} className="border-t border-gray-100">
                    <td className="px-2 py-1 font-mono">{row.booking_id}</td>
                    <td className="px-2 py-1">{row.tier || '—'}</td>
                    <td className="px-2 py-1">{row.status}</td>
                    <td className="px-2 py-1 text-right">{row.before?.net_fare ?? '—'}</td>
                    <td className="px-2 py-1 text-right">{row.after?.net_fare ?? '—'}</td>
                    <td className="px-2 py-1 text-right">{row.after?.airline_commission ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[240px_1fr] gap-4">
        {/* ── Left rail: report picker + presets ── */}
        <aside className="space-y-4">
          <div className="bg-white border border-gray-200 rounded-lg p-2">
            {Object.entries(types).map(([key, meta]) => {
              const Icon = REPORT_ICONS[key] || BarChart3;
              const active = key === activeReport;
              return (
                <button
                  key={key}
                  onClick={() => setActiveReport(key)}
                  data-testid={`report-type-${key}`}
                  className={`w-full text-left flex items-start gap-2 px-3 py-2 rounded-md text-sm transition-colors ${
                    active ? 'bg-[#0C1D36] text-white' : 'hover:bg-gray-50 text-gray-700'
                  }`}
                >
                  <Icon className={`w-4 h-4 mt-0.5 flex-shrink-0 ${active ? 'text-[#F5A623]' : 'text-gray-400'}`} />
                  <span className="flex-1">
                    <span className="block font-medium">{meta.label}</span>
                    <span className={`block text-xs mt-0.5 leading-tight ${active ? 'text-gray-300' : 'text-gray-500'}`}>{meta.description}</span>
                  </span>
                </button>
              );
            })}
          </div>

          {presets.length > 0 && (
            <div className="bg-white border border-gray-200 rounded-lg p-3">
              <div className="text-xs font-semibold text-gray-600 uppercase tracking-wide mb-2">Saved Presets</div>
              <div className="space-y-1">
                {presets.map((p) => (
                  <div key={p.id} className="flex items-center gap-1 group">
                    <button
                      onClick={() => applyPreset(p)}
                      className="flex-1 text-left text-xs px-2 py-1.5 rounded hover:bg-gray-50 truncate"
                      title={`${p.report_type} · ${p.name}`}
                      data-testid={`reports-preset-${p.id}`}
                    >
                      {p.name}
                    </button>
                    <button
                      onClick={() => deletePreset(p.id)}
                      className="opacity-0 group-hover:opacity-100 p-1 text-gray-400 hover:text-red-600"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            </div>
          )}
        </aside>

        {/* ── Main panel ── */}
        <main className="space-y-4 min-w-0">
          {/* Date presets + custom range */}
          <div className="bg-white border border-gray-200 rounded-lg p-3 flex flex-wrap items-center gap-2">
            <div className="flex items-center gap-1 text-xs text-gray-500 mr-1">
              <Calendar className="w-4 h-4" /> Period
            </div>
            {DATE_PRESETS.map((p) => (
              <button
                key={p.id}
                onClick={() => setPreset(p.id)}
                className={`px-3 py-1.5 rounded text-xs font-medium transition ${
                  preset === p.id ? 'bg-[#0C1D36] text-white' : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                }`}
                data-testid={`reports-date-${p.id}`}
              >
                {p.label}
              </button>
            ))}
            {preset === 'custom' && (
              <div className="flex items-center gap-2 ml-2">
                <input type="date" value={customFrom} onChange={(e) => setCustomFrom(e.target.value)}
                  className="px-2 py-1 text-xs border border-gray-300 rounded" data-testid="reports-date-from" />
                <span className="text-gray-400 text-xs">to</span>
                <input type="date" value={customTo} onChange={(e) => setCustomTo(e.target.value)}
                  className="px-2 py-1 text-xs border border-gray-300 rounded" data-testid="reports-date-to" />
              </div>
            )}
            <button onClick={run} className="ml-auto flex items-center gap-2 px-3 py-1.5 bg-white border border-gray-300 rounded text-xs hover:bg-gray-50" data-testid="reports-refresh">
              <RefreshCw className="w-3.5 h-3.5" /> Refresh
            </button>
          </div>

          {/* Hotel performance — same date range as reports (Phase R2) */}
          <div className="bg-white border border-gray-200 rounded-lg p-4" data-testid="admin-hotel-performance">
            <div className="flex items-start justify-between gap-2 mb-3">
              <div>
                <h2 className="text-lg font-bold text-[#0C1D36] flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-[#F5A623]" />
                  Hotel performance
                </h2>
                <p className="text-xs text-gray-500 mt-0.5">Cross-filtered by the period selector above.</p>
              </div>
              {hotelPerfLoading && <Loader2 className="w-5 h-5 animate-spin text-gray-400 flex-shrink-0" />}
            </div>
            {hotelPerf && hotelPerf.success && (
              <>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 mb-4 text-xs">
                  <div className="rounded-md bg-gray-50 border border-gray-100 p-2">
                    <div className="text-gray-500 uppercase tracking-wide">Bookings</div>
                    <div className="text-lg font-bold text-[#0C1D36]">{hotelPerf.summary?.total_hotel_bookings ?? 0}</div>
                  </div>
                  <div className="rounded-md bg-gray-50 border border-gray-100 p-2">
                    <div className="text-gray-500 uppercase tracking-wide">Confirmed revenue</div>
                    <div className="text-lg font-bold text-[#0C1D36]">
                      ₹{(hotelPerf.summary?.confirmed_revenue ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                  <div className="rounded-md bg-gray-50 border border-gray-100 p-2">
                    <div className="text-gray-500 uppercase tracking-wide">ADR</div>
                    <div className="text-lg font-bold text-[#0C1D36]">
                      ₹{(hotelPerf.hotel_stats?.adr ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                  <div className="rounded-md bg-gray-50 border border-gray-100 p-2">
                    <div className="text-gray-500 uppercase tracking-wide">Cancel rate</div>
                    <div className="text-lg font-bold text-[#0C1D36]">{hotelPerf.summary?.hotel_cancellation_rate ?? 0}%</div>
                  </div>
                  <div className="rounded-md bg-gray-50 border border-gray-100 p-2">
                    <div className="text-gray-500 uppercase tracking-wide">Supplier cancels</div>
                    <div className="text-lg font-bold text-[#0C1D36]">{hotelPerf.summary?.supplier_side_cancellation_rate ?? 0}%</div>
                  </div>
                  <div className="rounded-md bg-gray-50 border border-gray-100 p-2">
                    <div className="text-gray-500 uppercase tracking-wide">Markup</div>
                    <div className="text-lg font-bold text-[#0C1D36]">
                      ₹{(hotelPerf.summary?.hotel_markup ?? 0).toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                    </div>
                  </div>
                </div>

                {hotelPerf.revenue_by_day?.length > 0 && (
                  <div className="h-56 min-h-[220px] w-full min-w-0 mb-4">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={hotelPerf.revenue_by_day}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                        <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                        <YAxis tick={{ fontSize: 10 }} />
                        <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
                        <Bar dataKey="revenue" fill="#0d9488" name="Revenue" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">Top properties</h3>
                    <div className="overflow-x-auto border border-gray-100 rounded-md">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 text-gray-600">
                          <tr>
                            <th className="px-2 py-1.5 text-left">Property</th>
                            <th className="px-2 py-1.5 text-right">Bookings</th>
                            <th className="px-2 py-1.5 text-right">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {(hotelPerf.top_properties || []).map((p, i) => (
                            <tr key={i}>
                              <td className="px-2 py-1.5 text-gray-800">{p.hotel_name}</td>
                              <td className="px-2 py-1.5 text-right">{p.bookings}</td>
                              <td className="px-2 py-1.5 text-right font-medium">
                                ₹{p.revenue.toLocaleString('en-IN')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                  <div>
                    <h3 className="text-sm font-semibold text-gray-800 mb-2">Agents — hotel sales</h3>
                    <div className="overflow-x-auto border border-gray-100 rounded-md">
                      <table className="w-full text-xs">
                        <thead className="bg-gray-50 text-gray-600">
                          <tr>
                            <th className="px-2 py-1.5 text-left">Agent</th>
                            <th className="px-2 py-1.5 text-right">Bookings</th>
                            <th className="px-2 py-1.5 text-right">Revenue</th>
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {(hotelPerf.top_agents_by_hotel_revenue || []).map((a, i) => (
                            <tr key={i}>
                              <td className="px-2 py-1.5 text-gray-800 max-w-[180px] truncate" title={a.agent_label}>
                                {a.agent_label}
                              </td>
                              <td className="px-2 py-1.5 text-right">{a.bookings}</td>
                              <td className="px-2 py-1.5 text-right font-medium">
                                ₹{a.revenue.toLocaleString('en-IN')}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </>
            )}
            {!hotelPerfLoading && !hotelPerf && (
              <p className="text-xs text-gray-400">Hotel metrics unavailable for this range.</p>
            )}
          </div>

          {/* Filters */}
          <FilterBar
            filterOptions={filterOptions}
            filters={filters}
            setFilters={setFilters}
            search={search}
            setSearch={setSearch}
            groupBy={groupBy}
            setGroupBy={setGroupBy}
            groupByOptions={groupByOptions}
            activeReport={activeReport}
            needsAgentDropdown={needsAgentDropdown}
            agents={agents}
            isGstReport={isGstReport}
            availableFys={data?.available_fys}
          />

          {/* GST audience tabs */}
          {isGstReport && (
            <GstAudienceTabs
              activeTab={gstTab}
              setActiveTab={(tab) => {
                setGstTab(tab);
                const next = { ...filters };
                delete next.audience;
                delete next.invoice_type;
                if (tab === 'b2b') next.audience = 'B2B';
                else if (tab === 'b2c') next.audience = 'B2C';
                else if (tab === 'cn') next.invoice_type = 'CREDIT_NOTE';
                else if (tab === 'supp') next.invoice_type = 'SUPPLEMENTARY_INVOICE';
                setFilters(next);
              }}
              summary={data?.summary}
            />
          )}

          {loading && (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Loading report…
            </div>
          )}

          {!loading && data && (
            <>
              {activeReport === 'wallet_reconciliation' && data.summary && (
                <ReconciliationBanner summary={data.summary} />
              )}
              <SummaryStrip summary={data.summary} />
              {activeReport === 'credit_ledger' && data.aging && (
                <AgingStrip aging={data.aging} />
              )}

              {data.series?.length > 0 && (
                <div className="bg-white border border-gray-200 rounded-lg p-4">
                  <ReportChart reportType={activeReport} series={data.series} groupBy={groupBy} />
                </div>
              )}

              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
                <ReportTable
                  data={data}
                  activeReport={activeReport}
                  dateFrom={effectiveDate.from}
                  dateTo={effectiveDate.to}
                />
                {activeReport === 'bookings' && data.total > (data.limit || 50) && (
                  <Pagination page={data.page || 1} total={data.total} limit={data.limit || 50} onChange={setPage} />
                )}
              </div>
            </>
          )}

          {!loading && !data && (
            <div className="bg-white border border-gray-200 rounded-lg p-10 text-center text-gray-500">
              No data — adjust your filters and try again.
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

// ── Filter bar ────────────────────────────────────────────────────────────
const FilterBar = ({ filterOptions, filters, setFilters, search, setSearch, groupBy, setGroupBy, groupByOptions, activeReport, needsAgentDropdown, agents, isGstReport, availableFys }) => {
  const upd = (k, v) => setFilters({ ...filters, [k]: v || undefined });
  const isLedger = activeReport === 'credit_ledger';
  const isRecon = activeReport === 'wallet_reconciliation';
  const hideBookingFilters = isLedger || isRecon || isGstReport;

  if (isGstReport) {
    const fys = availableFys && availableFys.length ? availableFys : ['26-27', '25-26', '24-25'];
    const quarters = ['', '2026-Q1', '2026-Q2', '2026-Q3', '2026-Q4',
                      '2025-Q1', '2025-Q2', '2025-Q3', '2025-Q4'];
    return (
      <div className="bg-white border border-gray-200 rounded-lg p-3 grid grid-cols-2 md:grid-cols-6 gap-2">
        <div className="col-span-2 relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="search"
            placeholder="Search invoice #, booking, PNR, GSTIN…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-xs"
            data-testid="gst-report-search"
          />
        </div>
        <select
          value={filters.fy || ''}
          onChange={(e) => upd('fy', e.target.value)}
          className="px-2 py-2 border border-gray-300 rounded text-xs"
          data-testid="gst-report-fy"
        >
          <option value="">All FYs</option>
          {fys.map((f) => <option key={f} value={f}>FY 20{f}</option>)}
        </select>
        <select
          value={filters.quarter || ''}
          onChange={(e) => upd('quarter', e.target.value)}
          className="px-2 py-2 border border-gray-300 rounded text-xs"
          data-testid="gst-report-quarter"
        >
          {quarters.map((q) => <option key={q || 'all'} value={q}>{q ? q : 'All Quarters'}</option>)}
        </select>
        <input
          type="month"
          value={filters.month || ''}
          onChange={(e) => upd('month', e.target.value)}
          className="px-2 py-2 border border-gray-300 rounded text-xs"
          data-testid="gst-report-month"
          placeholder="Month"
        />
        {groupByOptions.length > 0 && (
          <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-2 py-2 border-2 border-[#F5A623] rounded text-xs font-semibold text-[#0C1D36]" data-testid="reports-group-by">
            {groupByOptions.map((g) => <option key={g} value={g}>Group: {g}</option>)}
          </select>
        )}
      </div>
    );
  }

  return (
    <div className="bg-white border border-gray-200 rounded-lg p-3 grid grid-cols-2 md:grid-cols-6 gap-2">
      {!hideBookingFilters && (
        <div className="col-span-2 relative">
          <SearchIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
          <input
            type="search"
            placeholder="Search booking_id, PNR, contact, agent…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded text-xs"
            data-testid="reports-search"
          />
        </div>
      )}
      {needsAgentDropdown && (
        <select
          value={filters.agent_id || ''}
          onChange={(e) => upd('agent_id', e.target.value)}
          className="col-span-2 px-2 py-2 border border-gray-300 rounded text-xs"
          data-testid="reports-filter-agent"
        >
          <option value="">All Agents</option>
          {agents.map((a) => (
            <option key={a.id} value={a.id}>
              {a.full_name || a.email} {a.agency_name ? `· ${a.agency_name}` : ''}
            </option>
          ))}
        </select>
      )}
      {!hideBookingFilters && (
        <>
          <select value={filters.supplier || ''} onChange={(e) => upd('supplier', e.target.value)} className="px-2 py-2 border border-gray-300 rounded text-xs" data-testid="reports-filter-supplier">
            <option value="">All Suppliers</option>
            {(filterOptions.suppliers || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.status || ''} onChange={(e) => upd('status', e.target.value)} className="px-2 py-2 border border-gray-300 rounded text-xs" data-testid="reports-filter-status">
            <option value="">All Statuses</option>
            {(filterOptions.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <select value={filters.payment_mode || ''} onChange={(e) => upd('payment_mode', e.target.value)} className="px-2 py-2 border border-gray-300 rounded text-xs" data-testid="reports-filter-pm">
            <option value="">All Payment Modes</option>
            {(filterOptions.payment_modes || []).map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </>
      )}
      {groupByOptions.length > 0 ? (
        <select value={groupBy} onChange={(e) => setGroupBy(e.target.value)} className="px-2 py-2 border-2 border-[#F5A623] rounded text-xs font-semibold text-[#0C1D36]" data-testid="reports-group-by">
          {groupByOptions.map((g) => <option key={g} value={g}>Group: {g}</option>)}
        </select>
      ) : <div />}
    </div>
  );
};

// ── Reconciliation banner ─────────────────────────────────────────────────
const ReconciliationBanner = ({ summary }) => {
  if (summary.reconciled === undefined) return null;
  if (summary.reconciled) {
    return (
      <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 flex items-center gap-2" data-testid="wallet-recon-ok">
        <CheckCircle2 className="w-5 h-5 text-emerald-600" />
        <div className="flex-1 text-sm">
          <span className="font-semibold text-emerald-800">Reconciled.</span>
          <span className="text-emerald-700 ml-1">
            System wallet balance matches transaction history.
          </span>
        </div>
      </div>
    );
  }
  return (
    <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 flex items-start gap-2" data-testid="wallet-recon-variance">
      <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
      <div className="flex-1 text-sm">
        <div className="font-semibold text-amber-900">Variance detected</div>
        <div className="text-amber-800 mt-0.5">
          Sum of all transactions = <b>₹{(summary.all_time_net_txns ?? 0).toLocaleString('en-IN')}</b> ·
          current wallet balance = <b>₹{(summary.system_wallet_balance ?? 0).toLocaleString('en-IN')}</b> ·
          delta = <b>₹{(summary.variance ?? 0).toLocaleString('en-IN')}</b>.
          This usually indicates manually-seeded balances or a missing transaction log.
        </div>
      </div>
    </div>
  );
};

// ── Aging strip (credit ledger) ────────────────────────────────────────────
const AgingStrip = ({ aging }) => {
  const BUCKET_COLORS = { '0-30': 'emerald', '31-60': 'amber', '61-90': 'orange', '90+': 'red' };
  return (
    <div className="grid grid-cols-4 gap-3" data-testid="credit-ledger-aging">
      {aging.map((b) => {
        const tone = BUCKET_COLORS[b.label] || 'gray';
        return (
          <div key={b.label} className="bg-white border border-gray-200 rounded-lg p-3">
            <div className={`text-xs font-semibold uppercase tracking-wide text-${tone}-700`}>
              {b.label} days
            </div>
            <div className="text-lg md:text-xl font-bold text-[#0C1D36] mt-0.5">
              ₹{(b.outstanding || 0).toLocaleString('en-IN')}
            </div>
            <div className="text-xs text-gray-500">{b.agents || 0} agents</div>
          </div>
        );
      })}
    </div>
  );
};

// ── Summary strip ─────────────────────────────────────────────────────────
const SummaryStrip = ({ summary }) => {
  if (!summary) return null;
  // `reconciled` is surfaced via the big banner; skip the tiny empty card.
  const entries = Object.entries(summary).filter(([k]) => k !== 'reconciled');
  const isMoney = (k) => /revenue|gmv|refund|ticket|amount|limit|used|balance|topups|debits|net|available|variance|txns|outstanding|spend|taxable|cgst|sgst|igst|tcs|tax|pass_through|invoice_value|reversed|commission|cost|margin|ssr|published/i.test(k)
                         && !/_count$/i.test(k) && !/take_rate/i.test(k);
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      {entries.map(([k, v]) => (
        <div key={k} className="bg-white border border-gray-200 rounded-lg p-4" data-testid={`report-kpi-${k}`}>
          <div className="text-xs text-gray-500 uppercase tracking-wide">{k.replace(/_/g, ' ')}</div>
          <div className="text-xl md:text-2xl font-bold text-[#0C1D36] mt-1">
            {typeof v === 'number' && isMoney(k) ? `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 0 })}` :
             typeof v === 'number' && /_pct$/i.test(k) ? `${v}%` :
             typeof v === 'number' ? v.toLocaleString('en-IN') : (v ?? '—')}
          </div>
        </div>
      ))}
    </div>
  );
};

// ── Chart ─────────────────────────────────────────────────────────────────
const ReportChart = ({ reportType, series, groupBy }) => {
  if (!series?.length) return null;

  // GST Report — stacked bar for CGST/SGST/IGST/TCS per period
  if (reportType === 'gst_report') {
    return (
      <ResponsiveContainer width="100%" height={280}>
        <BarChart data={series}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
          <Legend />
          <Bar dataKey="cgst" stackId="tax" fill="#0d9488" name="CGST" />
          <Bar dataKey="sgst" stackId="tax" fill="#06b6d4" name="SGST" />
          <Bar dataKey="igst" stackId="tax" fill="#F5A623" name="IGST" />
          <Bar dataKey="tcs"  stackId="tax" fill="#8b5cf6" name="TCS" />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Wallet reconciliation — dual-bar topups vs debits over time
  if (reportType === 'wallet_reconciliation') {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <BarChart data={series}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
          <XAxis dataKey="label" tick={{ fontSize: 11 }} />
          <YAxis tick={{ fontSize: 11 }} />
          <Tooltip formatter={(v) => `₹${Number(v).toLocaleString('en-IN')}`} />
          <Legend />
          <Bar dataKey="topups" fill="#10b981" name="Top-ups" />
          <Bar dataKey="debits" fill="#ef4444" name="Debits" />
        </BarChart>
      </ResponsiveContainer>
    );
  }

  // Pie for categorical distributions (payment_mode / supplier etc.)
  const isPie = (reportType === 'payment_mode') ||
                (reportType === 'sales' && ['supplier', 'payment_mode', 'user_type'].includes(groupBy)) ||
                (reportType === 'cancellation' && ['supplier', 'refund_method', 'reason'].includes(groupBy));

  const valKey = series[0].gmv !== undefined ? 'gmv'
    : series[0].net_revenue !== undefined ? 'net_revenue'
    : series[0].revenue !== undefined ? 'revenue'
    : 'cancellations';

  if (isPie) {
    return (
      <ResponsiveContainer width="100%" height={260}>
        <PieChart>
          <Pie data={series} dataKey={valKey} nameKey="label" outerRadius={100} innerRadius={55}>
            {series.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
          </Pie>
          <Tooltip formatter={(v) => valKey === 'revenue' ? `₹${Number(v).toLocaleString('en-IN')}` : v} />
          <Legend />
        </PieChart>
      </ResponsiveContainer>
    );
  }

  return (
    <ResponsiveContainer width="100%" height={260}>
      <BarChart data={series}>
        <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
        <XAxis dataKey="label" tick={{ fontSize: 11 }} />
        <YAxis tick={{ fontSize: 11 }} />
        <Tooltip formatter={(v, n) => n === 'revenue' || n === 'refund_amount' ? `₹${Number(v).toLocaleString('en-IN')}` : v} />
        <Legend />
        <Bar dataKey={valKey} fill="#0C1D36" />
      </BarChart>
    </ResponsiveContainer>
  );
};

// ── Data table ────────────────────────────────────────────────────────────
const ReportTable = ({ data, activeReport, dateFrom, dateTo }) => {
  const rows = data.rows || data.series || [];
  if (!rows.length) {
    return <div className="p-8 text-center text-gray-500 text-sm">No rows match this filter.</div>;
  }
  const headers = Object.keys(rows[0]);
  const showStatementBtn = activeReport === 'credit_ledger' && data.group_by === 'agent';

  const fmt = (v, k) => {
    if (v === null || v === undefined) return '—';
    if (typeof v === 'number') {
      if (/revenue|amount|gmv|spend|balance|limit|ticket|topup|debit|credit|refund|outstanding|available|net|taxable|cgst|sgst|igst|tcs|tax|pass_through|invoice_value/i.test(k)
          && !/_pct$/.test(k)
          && !/_count$/i.test(k)
          && k !== 'rate_pct') {
        return `₹${v.toLocaleString('en-IN', { maximumFractionDigits: 2 })}`;
      }
      if (/_pct|rate_pct/.test(k)) return `${v}%`;
      return v.toLocaleString('en-IN');
    }
    if (typeof v === 'string' && v.length > 60) return v.slice(0, 57) + '…';
    return v;
  };

  const downloadStatement = async (agentId, agentName) => {
    const url = `${API}/agent-statement/${agentId}?date_from=${dateFrom || ''}&date_to=${dateTo || ''}`;
    try {
      const r = await axios.get(url, { withCredentials: true, responseType: 'blob' });
      const blob = new Blob([r.data], { type: 'application/pdf' });
      const link = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = link;
      a.download = `statement_${(agentName || 'agent').replace(/\s+/g, '_')}.pdf`;
      document.body.appendChild(a); a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(link);
    } catch (_e) { /* silent */ }
  };

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-gray-50 text-xs uppercase text-gray-600 sticky top-0">
          <tr>
            {headers.map((h) => (
              <th key={h} className="px-3 py-2 text-left whitespace-nowrap font-semibold">
                {h.replace(/_/g, ' ')}
              </th>
            ))}
            {showStatementBtn && <th className="px-3 py-2 text-right font-semibold">Statement</th>}
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-100">
          {rows.map((r, i) => (
            <tr key={i} className="hover:bg-gray-50">
              {headers.map((h) => {
                const v = r[h];
                let cellClass = "px-3 py-2 text-xs text-gray-700 whitespace-nowrap";
                if (h === 'aging_bucket' && v) {
                  const bucketColor = v === '0-30' ? 'bg-emerald-100 text-emerald-800' :
                                      v === '31-60' ? 'bg-amber-100 text-amber-900' :
                                      v === '61-90' ? 'bg-orange-100 text-orange-900' :
                                      v === '90+' ? 'bg-red-100 text-red-900' :
                                      'bg-gray-100 text-gray-700';
                  return (
                    <td key={h} className={cellClass}>
                      <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${bucketColor}`}>{v}</span>
                    </td>
                  );
                }
                if (h === 'utilisation_pct' && typeof v === 'number') {
                  const bgColor = v > 80 ? 'bg-red-500' : v > 50 ? 'bg-amber-500' : 'bg-emerald-500';
                  return (
                    <td key={h} className={cellClass}>
                      <div className="flex items-center gap-2 min-w-[120px]">
                        <div className="flex-1 h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div className={`h-full ${bgColor}`} style={{ width: `${Math.min(v, 100)}%` }} />
                        </div>
                        <span className="text-xs font-semibold w-10 text-right">{v}%</span>
                      </div>
                    </td>
                  );
                }
                return (
                  <td key={h} className={cellClass}>{fmt(v, h)}</td>
                );
              })}
              {showStatementBtn && (
                <td className="px-3 py-2 text-right">
                  <button
                    onClick={() => downloadStatement(r.agent_id, r.agent_name)}
                    className="text-xs px-2 py-1 bg-[#0C1D36] text-white rounded hover:bg-[#0C1D36]/90 inline-flex items-center gap-1"
                    data-testid={`soa-download-${r.agent_id}`}
                  >
                    <Download className="w-3 h-3" /> PDF
                  </button>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

// ── GST audience tabs (All / B2B / B2C / Credit Notes / Supplementary) ────
const GstAudienceTabs = ({ activeTab, setActiveTab, summary }) => {
  const tabs = [
    { id: 'all',  label: 'All',                count: summary?.invoice_count ?? 0 },
    { id: 'b2b',  label: 'B2B',                count: summary?.b2b_count ?? 0 },
    { id: 'b2c',  label: 'B2C',                count: summary?.b2c_count ?? 0 },
    { id: 'cn',   label: 'Credit Notes',       count: summary?.credit_note_count ?? 0,
                  tone: 'red' },
    { id: 'supp', label: 'Supplementary',      count: summary?.supplementary_count ?? 0,
                  tone: 'amber' },
  ];
  return (
    <div className="bg-white border border-gray-200 rounded-lg p-1 inline-flex gap-1 flex-wrap" data-testid="gst-audience-tabs">
      {tabs.map((t) => {
        const active = (activeTab || 'all') === t.id;
        const activeCls = t.tone === 'red'   ? 'bg-red-700 text-white'
                        : t.tone === 'amber' ? 'bg-amber-600 text-white'
                        : 'bg-[#0C1D36] text-white';
        return (
          <button
            key={t.id}
            onClick={() => setActiveTab(t.id)}
            className={`px-4 py-2 rounded-md text-sm font-medium transition ${
              active ? activeCls : 'text-gray-600 hover:bg-gray-50'
            }`}
            data-testid={`gst-audience-${t.id}`}
          >
            {t.label}
            <span className={`ml-2 text-xs px-1.5 py-0.5 rounded ${
              active ? 'bg-white/20' : 'bg-gray-100 text-gray-600'
            }`}>{t.count}</span>
          </button>
        );
      })}
    </div>
  );
};

// ── Pagination ────────────────────────────────────────────────────────────
const Pagination = ({ page, total, limit, onChange }) => {
  const pages = Math.max(1, Math.ceil(total / limit));
  return (
    <div className="flex items-center justify-between p-3 bg-gray-50 border-t border-gray-200 text-xs text-gray-600">
      <div>Showing {((page - 1) * limit) + 1}-{Math.min(page * limit, total)} of {total.toLocaleString()}</div>
      <div className="flex items-center gap-2">
        <button onClick={() => onChange(Math.max(1, page - 1))} disabled={page <= 1}
          className="p-1 border border-gray-300 rounded disabled:opacity-40">
          <ChevronLeft className="w-4 h-4" />
        </button>
        <span>Page {page} / {pages}</span>
        <button onClick={() => onChange(Math.min(pages, page + 1))} disabled={page >= pages}
          className="p-1 border border-gray-300 rounded disabled:opacity-40">
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

export default ReportsDashboard;
